import { useState, useEffect, useRef } from "react";
import { Upload, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RestaurantBanner } from "@/hooks/useRestaurantBanners";

interface BannerFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  banner?: RestaurantBanner | null;
  onSave: (data: { title?: string; subtitle?: string; cta_text?: string; is_active: boolean }, imageFile?: File) => Promise<void>;
}

export function BannerFormDialog({
  open,
  onOpenChange,
  banner,
  onSave,
}: BannerFormDialogProps) {
  const isEditing = !!banner;
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [ctaText, setCtaText] = useState("PEÇA JÁ");
  const [isActive, setIsActive] = useState(true);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (banner) {
        setTitle(banner.title || "");
        setSubtitle(banner.subtitle || "");
        setCtaText(banner.cta_text || "PEÇA JÁ");
        setIsActive(banner.is_active);
        setPreviewUrl(banner.image_url);
        setImageFile(null);
      } else {
        setTitle("");
        setSubtitle("");
        setCtaText("PEÇA JÁ");
        setIsActive(true);
        setPreviewUrl(null);
        setImageFile(null);
      }
    }
  }, [banner, open]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setPreviewUrl(isEditing ? banner?.image_url || null : null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isEditing && !imageFile) {
      return;
    }
    
    setIsSaving(true);
    await onSave(
      {
        title: title || undefined,
        subtitle: subtitle || undefined,
        cta_text: ctaText || undefined,
        is_active: isActive,
      },
      imageFile || undefined
    );
    setIsSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Banner" : "Novo Banner"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Image Upload */}
          <div className="space-y-2">
            <Label>Imagem {!isEditing && "*"}</Label>
            <div 
              className="relative aspect-video bg-muted rounded-lg overflow-hidden border-2 border-dashed border-muted-foreground/25 hover:border-muted-foreground/50 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              {previewUrl ? (
                <>
                  <img 
                    src={previewUrl} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage();
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
                  <Upload className="h-8 w-8 mb-2" />
                  <p className="text-sm">Clique para fazer upload</p>
                  <p className="text-xs">Recomendado: 1920x1080 pixels</p>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Título (opcional)</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Promoção de Verão"
            />
          </div>

          {/* Subtitle */}
          <div className="space-y-2">
            <Label htmlFor="subtitle">Subtítulo (opcional)</Label>
            <Input
              id="subtitle"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="Ex: 20% de desconto em todos os pratos"
            />
          </div>

          {/* CTA Text */}
          <div className="space-y-2">
            <Label htmlFor="cta_text">Texto do Botão (CTA)</Label>
            <Input
              id="cta_text"
              value={ctaText}
              onChange={(e) => setCtaText(e.target.value)}
              placeholder="Ex: PEÇA JÁ"
            />
          </div>

          {/* Active */}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="space-y-0.5">
              <Label>Ativo</Label>
              <p className="text-sm text-muted-foreground">
                Banner aparecerá no screensaver
              </p>
            </div>
            <Switch checked={isActive} onCheckedChange={setIsActive} />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={isSaving || (!isEditing && !imageFile)}
            >
              {isSaving ? "Salvando..." : isEditing ? "Salvar" : "Criar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
